import { Box, Button, Link as MuiLink, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import { type ConsentValue, getStoredConsent, setConsent } from '~/analytics/consent';

export type CookieConsentProps = {
  /** URL da política de privacidade exibida no aviso. */
  privacyPolicyUrl?: string;
  /** Storybook/testes: força a exibição ignorando o localStorage e não persiste a decisão. */
  forceVisible?: boolean;
  /** Callback opcional disparado após a decisão do usuário. */
  onDecision?: (value: ConsentValue) => void;
};

/**
 * Banner de consentimento de cookies (LGPD). Enquanto o usuário não decide,
 * o analytics e o session replay do PostHog ficam desligados (o erro essencial segue cookieless) (vide `~/analytics/consent` + `App.tsx`).
 * Aparece apenas na primeira visita (ou até uma decisão ser registrada).
 */
export default function CookieConsent({
  privacyPolicyUrl = 'https://www.aride.com.br',
  forceVisible = false,
  onDecision
}: CookieConsentProps) {
  const [visible, setVisible] = useState(() => forceVisible || getStoredConsent() === null);

  const decide = (value: ConsentValue) => {
    if (!forceVisible) setConsent(value);
    setVisible(false);
    onDecision?.(value);
  };

  if (!visible) return null;

  return (
    <Paper
      aria-label="Aviso de cookies e privacidade"
      data-app-id="cookie-consent"
      elevation={8}
      role="dialog"
      sx={{
        bottom: { xs: 0, sm: 16 },
        borderRadius: { xs: 0, sm: 2 },
        left: { sm: 16, xs: 0 },
        maxWidth: 760,
        mx: 'auto',
        p: { sm: 3, xs: 2 },
        position: 'fixed',
        right: { sm: 16, xs: 0 },
        zIndex: (theme) => theme.zIndex.snackbar + 1
      }}
    >
      <Box sx={{ alignItems: { md: 'center' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }} variant="subtitle2">
            Sua privacidade
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Para manter o app estável, usamos monitoramento de erros (sempre ativo, sem dados pessoais). Com a sua
            permissão, também usamos análise de uso e gravação de sessão para melhorar sua experiência — você pode
            recusar quando quiser. Saiba mais na nossa{' '}
            <MuiLink
              data-app-id="cookie-consent-policy"
              href={privacyPolicyUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              política de privacidade
            </MuiLink>
            .
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0, gap: 1, width: { md: 'auto', xs: '100%' } }}>
          <Button data-app-id="cookie-consent-reject" fullWidth onClick={() => decide('rejected')} variant="outlined">
            Recusar
          </Button>
          <Button data-app-id="cookie-consent-accept" fullWidth onClick={() => decide('accepted')} variant="contained">
            Aceitar
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
